"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateEvent = void 0;
const axios_1 = __importDefault(require("axios"));
const core_1 = require("./events/core");
const types_1 = require("./events/types");
const PathReporter_1 = require("io-ts/PathReporter");
const Either_1 = require("fp-ts/Either");
class LoggingError extends Error {
    constructor(statusCode) {
        super(`Cannot send log event: could not send log [status=${statusCode ? statusCode : "unknown"}]`);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, LoggingError);
        }
        this.name = "LoggingError";
    }
}
class ValidationError extends Error {
    constructor(topic, eventData, result) {
        super([
            `Cannot send log event: could not validate log event [topic=${topic}]:`,
            ...PathReporter_1.PathReporter.report(result),
            `event data: ${JSON.stringify(eventData)}`,
        ].join("\n"));
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ValidationError);
        }
        this.name = "ValidationError";
    }
}
class TopicUnknownError extends Error {
    constructor(topic) {
        super(`Cannot send log event: topic unknown [topic=${topic}]`);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, TopicUnknownError);
        }
        this.name = "TopicUnknownError";
    }
}
class OriginMissingError extends Error {
    constructor() {
        super(`Cannot send log event: origin missing`);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, OriginMissingError);
        }
        this.name = "OriginMissingError";
    }
}
class UnauthorizedError extends Error {
    constructor() {
        super(`Cannot send log event: accessToken not set on MaydayLogsClient.`);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, UnauthorizedError);
        }
        this.name = "TopicUnknownError";
    }
}
class NoBaseURLError extends Error {
    constructor() {
        super(`Cannot send log event: no baseURL was set.`);
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, NoBaseURLError);
        }
        this.name = "NoBaseURLError";
    }
}
function validateEvent(topic, eventData) {
    const eventType = core_1.TOPIC_MAPPING[core_1.Topic[topic]];
    if (eventType === undefined) {
        throw new TopicUnknownError(topic);
    }
    const result = (0, core_1.createEventType)(eventType).decode(eventData);
    if (!(0, Either_1.isRight)(result)) {
        throw new ValidationError(topic, eventData, result);
    }
}
exports.validateEvent = validateEvent;
class MaydayLogsClient {
    constructor({ baseURL, accessToken, origin, lenient = true, axiosClient, customHeaders, clientIsConfig = false, }) {
        this.baseURL = baseURL;
        this.accessToken = accessToken;
        this.origin = origin ? String(types_1.Origin[origin]) : undefined;
        this.lenient = lenient;
        this.axiosClient = axiosClient ? axiosClient : axios_1.default;
        this.customHeaders = customHeaders ? customHeaders : {};
        this.clientIsConfig = clientIsConfig;
    }
    setAccessToken(token) {
        this.accessToken = token;
    }
    setOrigin(origin) {
        this.origin = String(types_1.Origin[origin]);
    }
    setCustomHeaders(headers) {
        this.customHeaders = headers;
    }
    validate({ topic, event, url, }) {
        if (!this.origin) {
            throw new OriginMissingError();
        }
        let eventPayload = {
            event,
            version: core_1.VERSION,
            origin: this.origin,
            topic: String(core_1.Topic[topic]),
            url: url,
            validation: true,
        };
        try {
            validateEvent(topic, eventPayload);
        }
        catch (error) {
            if (!this.lenient) {
                throw error;
            }
            eventPayload.validation = false;
            console.error(`Event failed to validate [topic=${topic}, origin=${this.origin}]\nError: ${error}`);
        }
        return eventPayload;
    }
    send(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const eventPayload = this.validate(params);
            if (this.accessToken === undefined && !this.clientIsConfig) {
                throw new UnauthorizedError();
            }
            if (this.baseURL === undefined && !this.clientIsConfig) {
                throw new NoBaseURLError();
            }
            try {
                const response = yield this.axiosClient.post(this.baseURL || "", eventPayload, {
                    headers: Object.assign(Object.assign({}, this.customHeaders), { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${this.accessToken}` }),
                });
                const statusCode = response.status;
                if (statusCode !== 200) {
                    throw new LoggingError(statusCode);
                }
            }
            catch (error) {
                console.error(error);
                throw new LoggingError();
            }
        });
    }
    sendMany(paramsArray) {
        return __awaiter(this, void 0, void 0, function* () {
            const eventsPayload = paramsArray.map((params) => this.validate(params));
            if (this.accessToken === undefined && !this.clientIsConfig) {
                throw new UnauthorizedError();
            }
            if (this.baseURL === undefined && !this.clientIsConfig) {
                throw new NoBaseURLError();
            }
            try {
                const response = yield this.axiosClient.post(`${this.baseURL || ""}/bulk`, { events: eventsPayload }, {
                    headers: Object.assign(Object.assign({}, this.customHeaders), { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${this.accessToken}` }),
                });
                const statusCode = response.status;
                if (statusCode !== 200) {
                    throw new LoggingError(statusCode);
                }
            }
            catch (error) {
                console.error(error);
                throw new LoggingError();
            }
        });
    }
    publicSend(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const eventPayload = this.validate(params);
            if (this.baseURL === undefined) {
                throw new NoBaseURLError();
            }
            try {
                const response = yield this.axiosClient.post(this.baseURL, eventPayload, {
                    headers: Object.assign(Object.assign({}, this.customHeaders), { "Content-Type": "application/json", Accept: "application/json" }),
                });
                const statusCode = response.status;
                if (statusCode !== 200) {
                    throw new LoggingError(statusCode);
                }
            }
            catch (error) {
                console.error(error);
                throw new LoggingError();
            }
        });
    }
}
exports.default = MaydayLogsClient;
